import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import {
  APPLY_DISCOUNT_CODE,
  CANCEL_DISCOUNT_CODE,
} from "../common/api/mutations";
import {
  CheckoutSession,
  ApplyDiscountCodeInput,
  ApplyDiscountCodeResponse,
  DiscountCodeReservationResponse,
  DiscountErrorCode,
} from "../common/types";

export const APPLIED_DISCOUNT_KEY = "appliedDiscountCode";
export const APPLIED_DISCOUNT_RESERVATION_KEY = "appliedDiscountReservationId";
export const APPLIED_DISCOUNT_AMOUNT = "appliedDiscountAmount";
export const APPLIED_DISCOUNT_SESSION_ID = "appliedDiscountSessionId";

export const useDiscountOperations = (checkoutSession: CheckoutSession) => {
  const [applyDiscountMutation] = useMutation(APPLY_DISCOUNT_CODE);
  const [cancelDiscountReservationMutation] = useMutation(CANCEL_DISCOUNT_CODE);

  // Apply Discount
  const getErrorMessage = (
    errorCode: DiscountErrorCode | undefined,
    originalMessage: string
  ): string => {
    switch (errorCode) {
      case DiscountErrorCode.STANDARD_ERROR:
        return "Discount Coupon code is not valid";
      case DiscountErrorCode.USER_ERROR:
        return originalMessage;
      case DiscountErrorCode.SYSTEM_ERROR:
        return "Cannot proceed with the action, please try again";
      default:
        return originalMessage;
    }
  };

  const applyDiscount = useCallback(
    async (
      input: ApplyDiscountCodeInput
    ): Promise<ApplyDiscountCodeResponse> => {
      const response = await applyDiscountMutation({
        variables: { input },
      });
      const result = response.data?.applyDiscountCodeToSession;

      if (!result) {
        return {
          success: false,
          message: "Failed to apply discount",
          errorCode: DiscountErrorCode.SYSTEM_ERROR,
        };
      }

      // Map error message if there's an error code
      if (!result.success && result.errorCode) {
        result.message = getErrorMessage(result.errorCode, result.message);
      }

      if (result.success && result.discountDetails) {
        // Store code, reservationId, amounts, and session ID
        localStorage.setItem(APPLIED_DISCOUNT_KEY, input.discountCode);
        localStorage.setItem(
          APPLIED_DISCOUNT_RESERVATION_KEY,
          result.discountDetails.reservationId
        );
        localStorage.setItem(
          APPLIED_DISCOUNT_AMOUNT,
          result.discountDetails.discountAmount.toString()
        );
        localStorage.setItem(APPLIED_DISCOUNT_SESSION_ID, checkoutSession.id);
      }

      return result;
    },
    [applyDiscountMutation]
  );

  // Cancel Reservation
  const cancelDiscountReservation = async (
    reservationId: string
  ): Promise<DiscountCodeReservationResponse> => {
    const response = await cancelDiscountReservationMutation({
      variables: {
        input: {
          reservationId,
        },
      },
    });
    const result = response.data?.cancelDiscountCodeReservation;

    if (result?.success) {
      // Clear all stored discount data
      localStorage.removeItem(APPLIED_DISCOUNT_KEY);
      localStorage.removeItem(APPLIED_DISCOUNT_RESERVATION_KEY);
      localStorage.removeItem(APPLIED_DISCOUNT_AMOUNT);
      localStorage.removeItem(APPLIED_DISCOUNT_SESSION_ID);
    }

    return result;
  };

  return {
    applyDiscount,
    cancelDiscountReservation,
  };
};
